/**
 * HALO - Dashboard Section Cards
 * KPI metric cards with trend indicators, inspired by shadcn's SectionCards.
 */

import React from "react";
import { TrendingUp, TrendingDown } from "lucide-react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardAction,
  CardFooter,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

interface SectionCardData {
  title: string;
  value: string | number;
  description: string;
  trend?: {
    value: string;
    direction: "up" | "down";
    isPositive: boolean;
  };
  footer: string;
  icon: React.ReactNode;
}

interface SectionCardsProps {
  cards: SectionCardData[];
}

export function SectionCards({ cards }: SectionCardsProps) {
  return (
    <div className="grid grid-cols-1 gap-4 px-4 sm:grid-cols-2 lg:px-6 xl:grid-cols-4">
      {cards.map((card, idx) => (
        <Card
          key={idx}
          className="bg-gradient-to-t from-primary/5 to-surface-dark"
        >
          <CardHeader>
            <CardDescription className="flex items-center gap-2">
              {card.icon}
              {card.title}
            </CardDescription>
            <CardTitle className="text-2xl font-semibold tabular-nums">
              {card.value}
            </CardTitle>
            {card.trend && (
              <CardAction>
                <Badge
                  variant={card.trend.isPositive ? "success" : "destructive"}
                >
                  {card.trend.direction === "up" ? (
                    <TrendingUp className="w-3.5 h-3.5" />
                  ) : (
                    <TrendingDown className="w-3.5 h-3.5" />
                  )}
                  {card.trend.value}
                </Badge>
              </CardAction>
            )}
          </CardHeader>
          <CardFooter className="flex-col items-start gap-1.5 text-sm">
            <div className="line-clamp-1 flex gap-2 font-medium text-foreground">
              {card.description}
              {card.trend && (
                <>
                  {card.trend.direction === "up" ? (
                    <TrendingUp className="w-4 h-4" />
                  ) : (
                    <TrendingDown className="w-4 h-4" />
                  )}
                </>
              )}
            </div>
            <div className="text-muted-slate text-xs">{card.footer}</div>
          </CardFooter>
        </Card>
      ))}
    </div>
  );
}
